// =============================================================================
// Default AppConfig — production-safe values used to seed a fresh environment.
// Loaded by server `ensureAppConfig()` on first boot; a stored config wins.
// =============================================================================

import type { AppConfig } from '../types';
import { COINS, ENERGY } from './economy';

/**
 * The mobile app's current version. When raising the client version, bump this
 * in lockstep with `mobile/app.json` so fresh environments don't lock players
 * out of a build that ships with the bump.
 */
export const DEFAULT_MIN_SUPPORTED_VERSION = '0.1.0';

export const DEFAULT_APP_CONFIG: Omit<AppConfig, 'version' | 'updatedAt'> = {
  minimumVersion: {
    ios: DEFAULT_MIN_SUPPORTED_VERSION,
    android: DEFAULT_MIN_SUPPORTED_VERSION,
  },
  ads: {
    // Which ad SDK the mobile client initializes (both ship in the binary).
    // 'admob' is the failsafe: AdMob is the only provider that has ever served
    // an ad in production, and it is the one the published privacy policy
    // names. The previous failsafe was 'applovin', which meant a fresh install
    // whose config fetch was still in flight initialized AppLovin and LOCKED
    // it for that session — serving ads from a network the policy does not
    // name, with no consent flag set (#548). Do NOT flip this back to
    // 'applovin' without updating `games/word-clash/site/privacy.html` and the
    // `privacyPolicy.*` / `terms.*` blocks in `mobile/src/i18n/{en,tr}.ts` in
    // the same change.
    provider: 'admob',
    banner: { enabled: true },
    interstitial: { enabled: true },
    rewarded: { enabled: true },
  },
  maintenance: {
    enabled: false,
  },
  killSwitch: {
    global: false,
  },
  observability: {
    // Failsafe ON — a config-fetch failure on the client must NOT silently
    // break crash telemetry. Flip to `false` only as a deliberate kill switch
    // for Firebase Crashlytics ingestion (#429).
    crashlyticsEnabled: true,
    funnelTelemetryEnabled: true,
  },
  // Gameplay presentation. 'right' = own answers on the right, matching the
  // messaging-app convention and the in-app DM thread; 'left' is the legacy
  // layout, kept selectable so the flip can be reverted remotely.
  gameplay: {
    answerFeedSelfSide: 'right',
  },
  // Shell presentation. 'classic' = the shipped Home; 'hero' opts a game into
  // the shared hero Home (#875) once it registers `branding.homeHero`.
  ui: {
    homeLayout: 'classic',
  },
  // Energy economy defaults mirror the `ENERGY.*` constants — the admin config
  // UI overrides these live without an app build (server-authoritative).
  economy: {
    maxEnergy: ENERGY.MAX,
    regenIntervalMinutes: ENERGY.REGEN_INTERVAL_MINUTES,
    startingEnergy: ENERGY.STARTING,
    matchCostPerMatch: ENERGY.COST_PER_MATCH,
    dailyChallengeRequiredWins: COINS.DAILY_BONUS_REQUIRED_WINS,
    matchWinCoins: COINS.MATCH_WIN_REWARD,
    matchLossCoins: COINS.MATCH_LOSS_REWARD,
    adEnergyRewardAmount: ENERGY.AD_REWARD_AMOUNT,
  },
};
